import { useCallback, useEffect, useMemo, useState } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { fetchCategories } from "src/services/EngagementAPIs";
import { getApiLangFromI18n } from "src/utils/apiLang";
import { CATEGORIES_PAGE_SIZE } from "../AiCampaignConstants";
import { formatCategoryName } from "../AiCampaignUtils";

export const useProductCategories = (
  selectedCategories: string[],
  onSelectionChange: (_categories: string[]) => void,
  enabled = true,
  maxSelection?: number
) => {
  const { i18n } = useTranslation();
  const [categories, setCategories] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const loadCategories = useCallback(async () => {
    setIsLoading(true);
    setHasError(false);
    try {
      const response = await fetchCategories(getApiLangFromI18n(i18n));
      setCategories(response?.website_categories ?? []);
    } catch {
      message.error(i18n.t("campaignFlow.aiEmailEditor.fetchCategoriesFailed"));
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, [i18n]);

  useEffect(() => {
    if (enabled) {
      loadCategories();
    }
  }, [enabled, loadCategories]);

  const selectedSet = useMemo(() => new Set(selectedCategories), [selectedCategories]);

  const filteredCategories = useMemo(() => {
    const terms = searchTerm.toLowerCase().split(/\s+/).filter(Boolean);
    if (!terms.length) {
      return categories;
    }
    return categories.filter(category => {
      const words = formatCategoryName(category).toLowerCase().split(/[^\p{L}\p{N}]+/u);
      return terms.every(term => words.some(word => word.startsWith(term)));
    });
  }, [categories, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredCategories.length / CATEGORIES_PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);

  const pageCategories = useMemo(() => {
    const start = (safePage - 1) * CATEGORIES_PAGE_SIZE;
    return filteredCategories.slice(start, start + CATEGORIES_PAGE_SIZE);
  }, [filteredCategories, safePage]);

  const isAllOnScreenSelected =
    pageCategories.length > 0 && pageCategories.every(category => selectedSet.has(category));
  const isAllSelected =
    categories.length > 0 && categories.every(category => selectedSet.has(category));

  const isLimitReached = maxSelection !== undefined && selectedCategories.length >= maxSelection;

  const toggleCategory = useCallback(
    (category: string) => {
      if (selectedSet.has(category)) {
        onSelectionChange(selectedCategories.filter(item => item !== category));
      } else if (!isLimitReached) {
        onSelectionChange([...selectedCategories, category]);
      }
    },
    [isLimitReached, onSelectionChange, selectedCategories, selectedSet]
  );

  const toggleAllOnScreen = useCallback(() => {
    if (isAllOnScreenSelected) {
      const pageSet = new Set(pageCategories);
      onSelectionChange(selectedCategories.filter(category => !pageSet.has(category)));
      return;
    }
    onSelectionChange(Array.from(new Set([...selectedCategories, ...pageCategories])));
  }, [isAllOnScreenSelected, onSelectionChange, pageCategories, selectedCategories]);

  const toggleAll = useCallback(() => {
    onSelectionChange(isAllSelected ? [] : categories);
  }, [categories, isAllSelected, onSelectionChange]);

  const updateSearchTerm = useCallback((term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  }, []);

  const goToPreviousPage = useCallback(() => setCurrentPage(Math.max(1, safePage - 1)), [safePage]);
  const goToNextPage = useCallback(
    () => setCurrentPage(Math.min(totalPages, safePage + 1)),
    [safePage, totalPages]
  );

  return {
    categories,
    pageCategories,
    selectedSet,
    isLoading,
    hasError,
    searchTerm,
    currentPage: safePage,
    totalPages,
    isAllOnScreenSelected,
    isAllSelected,
    isLimitReached,
    loadCategories,
    toggleCategory,
    toggleAllOnScreen,
    toggleAll,
    updateSearchTerm,
    goToPreviousPage,
    goToNextPage,
  };
};

export type ProductCategoriesState = ReturnType<typeof useProductCategories>;
